import { v4 } from "uuid";
import { useMemo } from "react";
import { API } from "aws-amplify";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import dayjsTZ from "../../../utils/dayjs";
import { useAuth } from "../../../authentication/authHelpers";
import { programFields as programFieldsReducer } from "../../../store/actions";

/**
 * fieldOptions of this program field:
 * {
 *   [reportType]: { title, subTitle, note, columnDefs, allAppliedColumnDefs }, // shared default
 *   userTemplates: {
 *     [userSub]: {
 *       [reportType]: {
 *         activeTemplateId: string | null, // null -> shared default
 *         templates: [{ id, name, title, subTitle, note, columnDefs, createdAt, updatedAt }],
 *       },
 *     },
 *   },
 * }
 */
export const REPORT_FILTERS_FIELD_ID = "0d452d-1f23tut-4b2d6gt5-a7d8e8w6";

export const DEFAULT_TEMPLATE_ID = "default";
export const DEFAULT_TEMPLATE_LABEL = "Parazgjedhur";

// the part of a report's configuration that a template overrides
export const pickTemplateValues = (source = {}) => ({
  title: source.title || "",
  subTitle: source.subTitle || "",
  note: source.note || "",
  columnDefs: source.columnDefs || [],
});

export const getTemplateOptions = (templates = []) => [
  { value: DEFAULT_TEMPLATE_ID, label: DEFAULT_TEMPLATE_LABEL },
  ...templates.map(({ id, name }) => ({ value: id, label: name })),
];

export const useReportTemplates = () => {
  const dispatch = useDispatch();
  const { reportType } = useParams();
  const { user } = useAuth() || {};
  const { programFields } = useSelector((state) => state.programFields);

  const userSub = user?.userSub || null;

  const fieldOptions = useMemo(
    () =>
      (programFields || []).find(
        ({ fieldId }) => fieldId === REPORT_FILTERS_FIELD_ID
      )?.fieldOptions || {},
    [programFields]
  );

  const baseConfig = fieldOptions[reportType] || {};
  const { activeTemplateId = null, templates = [] } =
    fieldOptions.userTemplates?.[userSub]?.[reportType] || {};
  const activeTemplate =
    templates.find(({ id }) => id === activeTemplateId) || null;

  // same shape as the stored fieldOptions (keyed by report type), with the
  // user's active template laid over the shared configuration of this report
  const constants = useMemo(() => {
    const { userTemplates, ...reports } = fieldOptions;
    if (!activeTemplate || !reports[reportType]) return reports;
    return {
      ...reports,
      [reportType]: {
        ...reports[reportType],
        ...pickTemplateValues(activeTemplate),
      },
    };
  }, [fieldOptions, reportType, activeTemplate]);

  // every user writes into the same record, so merge into the live copy
  // rather than the redux one to avoid overwriting someone else's templates
  const updateUserTemplates = async (updater) => {
    if (!userSub) throw new Error("No authenticated user to save templates for");

    const field = await API.get(
      "programFields",
      `/programFields/${REPORT_FILTERS_FIELD_ID}`
    );
    const latest = field?.fieldOptions || {};
    const allUsers = latest.userTemplates || {};
    const mine = allUsers[userSub] || {};
    const current = {
      activeTemplateId: null,
      templates: [],
      ...(mine[reportType] || {}),
    };

    const nextFieldOptions = {
      ...latest,
      userTemplates: {
        ...allUsers,
        [userSub]: { ...mine, [reportType]: updater(current) },
      },
    };

    await API.put(
      "programFields",
      `/programFields/${REPORT_FILTERS_FIELD_ID}`,
      { body: { fieldOptions: nextFieldOptions } }
    );
    dispatch(
      programFieldsReducer(
        (programFields || []).map((pf) =>
          pf.fieldId === REPORT_FILTERS_FIELD_ID
            ? { ...pf, fieldOptions: nextFieldOptions }
            : pf
        )
      )
    );
  };

  // creates the template when it has no id, updates it otherwise; either way
  // the saved template becomes the active one
  const saveTemplate = async ({ id, name, ...values }) => {
    const templateId = id || v4();
    const now = dayjsTZ().valueOf();
    const template = {
      ...pickTemplateValues(values),
      id: templateId,
      name: name.trim(),
      updatedAt: now,
    };

    await updateUserTemplates((current) => ({
      activeTemplateId: templateId,
      templates: current.templates.some((t) => t.id === templateId)
        ? current.templates.map((t) =>
            t.id === templateId ? { ...t, ...template } : t
          )
        : [...current.templates, { ...template, createdAt: now }],
    }));
    return templateId;
  };

  const deleteTemplate = (templateId) =>
    updateUserTemplates((current) => ({
      activeTemplateId:
        current.activeTemplateId === templateId
          ? null
          : current.activeTemplateId,
      templates: current.templates.filter((t) => t.id !== templateId),
    }));

  const switchTemplate = (templateId) =>
    updateUserTemplates((current) => ({
      ...current,
      activeTemplateId: current.templates.some((t) => t.id === templateId)
        ? templateId
        : null,
    }));

  return {
    userSub,
    constants,
    templates,
    baseConfig,
    activeTemplateId: activeTemplate?.id || DEFAULT_TEMPLATE_ID,
    saveTemplate,
    deleteTemplate,
    switchTemplate,
  };
};
